import { useEffect, useState } from 'react';
import type { ExtractedImage, SimBackdropSelection, SimulationEnvironment } from '@ddp/shared';

import { apiFetch } from '../../api/client.js';
import { useT } from '../../i18n/index.js';

/** mm:ss depuis un décalage en millisecondes (comme ailleurs dans l'app). */
function fmtMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Curation du **fond** du Récapitulatif (ADR-0033) : associe une image de la base à l'environnement
 * courant. Cloisonné (on ne voit que les images de son organisation) ; l'association est persistée
 * côté serveur et vaut pour toute l'organisation.
 */
export function BackdropPicker({
  environment,
  environmentLabel,
  images,
  canEdit,
  onChanged,
}: {
  environment: SimulationEnvironment;
  environmentLabel: string;
  images: ExtractedImage[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const t = useT();
  const [selections, setSelections] = useState<SimBackdropSelection[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    apiFetch<SimBackdropSelection[]>('/simulation/backdrops')
      .then((s) => {
        if (active) setSelections(s);
      })
      .catch(() => {
        if (active) setSelections([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const currentId = selections.find((s) => s.environment === environment)?.imageId ?? null;
  const current = currentId ? images.find((im) => im.id === currentId) : null;

  const choose = async (imageId: string) => {
    setBusy(true);
    try {
      await apiFetch<SimBackdropSelection>(`/simulation/backdrops/${environment}`, {
        method: 'PUT',
        body: { imageId },
      });
      setSelections((prev) => [
        ...prev.filter((s) => s.environment !== environment),
        { environment, imageId },
      ]);
      setOpen(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await apiFetch(`/simulation/backdrops/${environment}`, { method: 'DELETE' });
      setSelections((prev) => prev.filter((s) => s.environment !== environment));
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ddp-backdrop">
      <div className="ddp-backdrop__row">
        <span className="ddp-backdrop__label">{t('sim.backdrop.label')}</span>
        <span className="ddp-backdrop__value">
          {current ? current.videoTitle : t('sim.backdrop.none')}
        </span>
        {canEdit && (
          <span className="ddp-backdrop__actions">
            <button
              type="button"
              className="ddp-backdrop__btn"
              disabled={busy}
              onClick={() => setOpen(true)}
            >
              {t('sim.backdrop.choose')}
            </button>
            {current && (
              <button
                type="button"
                className="ddp-backdrop__btn ddp-backdrop__btn--ghost"
                disabled={busy}
                onClick={() => void clear()}
              >
                {t('sim.backdrop.remove')}
              </button>
            )}
          </span>
        )}
      </div>

      {open && (
        <div
          className="ddp-backdrop__overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('sim.backdrop.modalTitle')}
        >
          <div className="ddp-backdrop__modal">
            <div className="ddp-backdrop__modal-head">
              <span>
                {t('sim.backdrop.modalTitle')} · <strong>{environmentLabel}</strong>
              </span>
              <button
                type="button"
                className="ddp-backdrop__btn ddp-backdrop__btn--ghost"
                onClick={() => setOpen(false)}
              >
                {t('sim.backdrop.close')}
              </button>
            </div>
            {images.length === 0 ? (
              <p className="ddp-backdrop__empty">{t('sim.backdrop.empty')}</p>
            ) : (
              <ul className="ddp-backdrop__grid">
                {images.map((im) => (
                  <li key={im.id}>
                    <button
                      type="button"
                      className={`ddp-backdrop__thumb${im.id === currentId ? ' is-on' : ''}`}
                      disabled={busy}
                      aria-pressed={im.id === currentId}
                      onClick={() => void choose(im.id)}
                    >
                      <img src={im.url} alt="" loading="lazy" />
                      <span className="ddp-backdrop__thumb-cap">
                        {im.videoTitle} · {fmtMs(im.timestampMs)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
